import { formatDate } from '@cvo/domain';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { computed, ref, type Ref, toValue, type MaybeRefOrGetter } from 'vue';

interface UseDateFormatOptions {
  dateFormat?: Intl.DateTimeFormatOptions['dateStyle'];
  timeFormat?: Intl.DateTimeFormatOptions['timeStyle'];
  fallback?: string;
  locale?: Ref<string>;
  timeZone?: string;
}

const DEFAULT_FORMATTING: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
};

/**
 * Composable for formatting dates with internationalization support
 * @param options - Date and time formatting options (can be reactive)
 * @returns Function to format dates with current locale
 */
export function useDateFormat(dateToFormat: MaybeRefOrGetter<Date | undefined | null>, options: MaybeRefOrGetter<UseDateFormatOptions> = {}) {
  let locale: Ref<string>;
  try {
    const i18n = useI18n();
    locale = i18n.locale;
  } catch {
    locale = toValue(options).locale || ref('en-US');
  }

  const formatOptions = computed<Intl.DateTimeFormatOptions>(() => {
    let opts: Intl.DateTimeFormatOptions = {};
    const resolvedOptions = toValue(options);

    if (resolvedOptions.dateFormat) {
      opts.dateStyle = resolvedOptions.dateFormat;
    }

    if (resolvedOptions.timeFormat) {
      opts.timeStyle = resolvedOptions.timeFormat;
    }

    // Fallback to custom format if no dateStyle/timeStyle is specified
    if (!resolvedOptions.dateFormat && !resolvedOptions.timeFormat) {
      opts = { ...DEFAULT_FORMATTING };
    }

    if (resolvedOptions.timeZone) {
      opts.timeZone = resolvedOptions.timeZone;
    }

    return opts;
  });

  const formattedDate = computed<string>(() => {
    const date = toValue(dateToFormat);
    const isInvalidDate = !date || isNaN(date.getTime());

    if (isInvalidDate) {
      return toValue(options).fallback ?? '';
    }

    return formatDate(date, locale.value, formatOptions.value);
  });

  return formattedDate;
}
